import React, { useState, useEffect } from 'react';
import axios from './lib/axiosClient';
import { useTenant } from './contexts/TenantContext';

const EXPENSE_CATS = [
  { id: 'rent', label: '🏢 Rent / Lease' },
  { id: 'utilities', label: '💡 Utilities' },
  { id: 'insurance', label: '🛡️ Insurance' },
  { id: 'payroll', label: '👥 Payroll' },
  { id: 'vendor_payment', label: '📦 Vendor Payment' },
  { id: 'inventory_purchase', label: '🛒 Inventory Purchase' },
  { id: 'supplies', label: '🧹 Supplies' },
  { id: 'marketing', label: '📣 Marketing' },
  { id: 'software', label: '💻 Software' },
  { id: 'maintenance', label: '🔧 Maintenance' },
  { id: 'shipping', label: '🚚 Shipping' },
  { id: 'banking', label: '🏦 Banking / Fees' },
  { id: 'taxes', label: '📋 Taxes' },
  { id: 'other', label: '📎 Other' },
];

const INCOME_CATS = [
  { id: 'sales', label: '🛍️ Product Sales' },
  { id: 'service', label: '💼 Service Income' },
  { id: 'labor', label: '🔧 Labor' },
  { id: 'tips', label: '💵 Tips' },
  { id: 'rental', label: '🏠 Rental Income' },
  { id: 'other_income', label: '📎 Other Income' },
];

const S = {
  bg: '#0f0f11', panel: '#16161a', border: '#2a2a35', text: '#f0f0f2',
  muted: '#7a7a8a', amber: '#f5a623', green: '#3ecf8e', red: '#e8484a',
};

export default function Finance() {
  const { tenant } = useTenant();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [filter, setFilter] = useState('month');
  const [form, setForm] = useState({ type: 'expense', category: 'vendor_payment', amount: '', description: '', date: new Date().toISOString().split('T')[0], notes: '' });

  useEffect(() => { load(); }, [tenant]);

  async function load() {
    setLoading(true);
    try {
      const r = await axios.get('/api/finance');
      setEntries(r.data.entries || []);
    } catch(e) { setEntries([]); }
    setLoading(false);
  }

  async function save() {
    if (!form.amount || !form.date) return;
    try {
      if (editEntry) {
        await axios.put('/api/finance/' + editEntry.id, { ...form, amount: parseFloat(form.amount) });
      } else {
        await axios.post('/api/finance', { ...form, amount: parseFloat(form.amount) });
      }
      setShowModal(false); setEditEntry(null);
      setForm({ type: 'expense', category: 'vendor_payment', amount: '', description: '', date: new Date().toISOString().split('T')[0], notes: '' });
      load();
    } catch(e) {}
  }

  async function del(id) {
    if (!window.confirm('Delete this entry?')) return;
    await axios.delete('/api/finance/' + id);
    load();
  }

  function openEdit(e) {
    setEditEntry(e);
    setForm({ type: e.type, category: e.category, amount: e.amount, description: e.description || '', date: e.date?.split('T')[0] || '', notes: e.notes || '' });
    setShowModal(true);
  }

  function filtered() {
    const now = new Date();
    return entries.filter(e => {
      const d = new Date(e.date);
      if (filter === 'week') { const w = new Date(now); w.setDate(now.getDate()-7); return d >= w; }
      if (filter === 'month') return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      if (filter === 'year') return d.getFullYear() === now.getFullYear();
      return true;
    });
  }

  const rows = filtered();
  const totalIn = rows.filter(e => e.type === 'income').reduce((s,e) => s+parseFloat(e.amount||0), 0);
  const totalOut = rows.filter(e => e.type === 'expense').reduce((s,e) => s+parseFloat(e.amount||0), 0);
  const net = totalIn - totalOut;

  const inp = { width: '100%', padding: '9px 12px', background: '#1a1a22', border: '1px solid ' + S.border, borderRadius: 8, color: S.text, fontSize: 14, boxSizing: 'border-box', outline: 'none' };
  const cats = form.type === 'expense' ? EXPENSE_CATS : INCOME_CATS;

  return (
    <div style={{ padding: 24, background: S.bg, minHeight: '100vh', color: S.text }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>💰 Finance</div>
          <div style={{ fontSize: 13, color: S.muted, marginTop: 2 }}>Track every dollar in and out</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {['week','month','year','all'].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12, background: filter === f ? S.amber : S.panel, color: filter === f ? '#000' : S.muted }}>{f.charAt(0).toUpperCase()+f.slice(1)}</button>
          ))}
          <button onClick={() => { setEditEntry(null); setForm({ type: 'income', category: 'sales', amount: '', description: '', date: new Date().toISOString().split('T')[0], notes: '' }); setShowModal(true); }} style={{ padding: '6px 14px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>+ Income</button>
          <button onClick={() => { setEditEntry(null); setForm({ type: 'expense', category: 'vendor_payment', amount: '', description: '', date: new Date().toISOString().split('T')[0], notes: '' }); setShowModal(true); }} style={{ padding: '6px 14px', background: S.red, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>+ Expense</button>
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 24 }}>
        {[['Money In', totalIn, S.green], ['Money Out', totalOut, S.red], ['Net', net, net >= 0 ? S.green : S.red]].map(([label, val, color]) => (
          <div key={label} style={{ background: S.panel, border: '1px solid ' + S.border, borderRadius: 12, padding: '18px 20px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: S.muted, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 8 }}>{label}</div>
            <div style={{ fontSize: 28, fontWeight: 900, color }}>{val < 0 ? '-' : ''}${Math.abs(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        ))}
      </div>

      {/* Entries table */}
      <div style={{ background: S.panel, border: '1px solid ' + S.border, borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '13px 18px', borderBottom: '1px solid ' + S.border, fontWeight: 700, fontSize: 13 }}>Transactions</div>
        {loading ? <div style={{ padding: 40, textAlign: 'center', color: S.muted }}>Loading...</div> : rows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: S.muted }}>No entries for this period</div>
        ) : rows.map(e => (
          <div key={e.id} style={{ display: 'flex', alignItems: 'center', padding: '12px 18px', borderBottom: '1px solid #1a1a22', gap: 12 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: e.type === 'income' ? S.green : S.red, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: S.text }}>{e.description || e.category}</div>
              <div style={{ fontSize: 11, color: S.muted }}>{e.category} · {e.date?.split('T')[0]}</div>
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, color: e.type === 'income' ? S.green : S.red }}>
              {e.type === 'income' ? '+' : '-'}${parseFloat(e.amount).toFixed(2)}
            </div>
            <button onClick={() => openEdit(e)} style={{ background: 'none', border: '1px solid ' + S.border, color: S.muted, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>Edit</button>
            <button onClick={() => del(e.id)} style={{ background: 'none', border: 'none', color: S.red, cursor: 'pointer', fontSize: 16 }}>×</button>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: S.panel, border: '1px solid ' + S.border, borderRadius: 14, padding: 28, width: 420, maxWidth: '95vw' }}>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 20 }}>{editEntry ? 'Edit Entry' : '+ New Entry'}</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              {['income','expense'].map(t => (
                <button key={t} onClick={() => setForm(f => ({ ...f, type: t, category: t === 'income' ? 'sales' : 'vendor_payment' }))}
                  style={{ flex: 1, padding: '8px', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', background: form.type === t ? (t === 'income' ? S.green : S.red) : S.bg, color: form.type === t ? '#fff' : S.muted }}>
                  {t.charAt(0).toUpperCase()+t.slice(1)}
                </button>
              ))}
            </div>
            {[['Category', <select value={form.category} onChange={e => setForm(f=>({...f,category:e.target.value}))} style={inp}>{cats.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}</select>],
              ['Description', <input value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} placeholder="Description..." style={inp}/>],
              ['Amount', <input type="number" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))} placeholder="0.00" style={inp}/>],
              ['Date', <input type="date" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))} style={inp}/>],
              ['Notes', <textarea value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} style={{...inp,minHeight:60,resize:'vertical'}}/>],
            ].map(([label, el]) => (
              <div key={label} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: S.muted, textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
                {el}
              </div>
            ))}
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={() => { setShowModal(false); setEditEntry(null); }} style={{ flex: 1, padding: 12, background: 'transparent', color: S.muted, border: '1px solid ' + S.border, borderRadius: 10, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={save} style={{ flex: 2, padding: 12, background: S.amber, color: '#000', border: 'none', borderRadius: 10, fontWeight: 800, cursor: 'pointer' }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
